import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import NavigationButton from "../../classResults/components/lessonNavigation/NavigationButton";

const WeekSelector = ({ selectedWeek, onWeekChange, mainClass }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [hoveredWeek, setHoveredWeek] = useState(null);
  const calendarRef = useRef(null);
  
  // 제한 날짜 정의 (2025년 1월 1일)
  const limitDate = new Date(2025, 0, 1);
  
  // 현재 날짜 정보 (이번 주 시작일)
  const getCurrentWeekStart = () => {
    const now = new Date();
    const currentDay = now.getDay();
    const diffToMonday = currentDay === 0 ? -6 : 1 - currentDay;
    
    const mondayDate = new Date(now);
    mondayDate.setDate(now.getDate() + diffToMonday);
    return mondayDate;
  };
  
  // 현재 월과 비교하기 위한 정보
  const currentDate = new Date();
  const currentMonth = currentDate.getFullYear() * 12 + currentDate.getMonth();
  
  // 선택된 주간이 변경될 때 달력 날짜도 업데이트
  useEffect(() => {
    if (selectedWeek.startDate) {
      const startDate = new Date(selectedWeek.startDate.replace(/\./g, '-'));
      setCalendarDate(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
    }
  }, [selectedWeek]);
  
  // 날짜가 제한 날짜보다 이전인지 확인
  const isBeforeLimitDate = (date) => {
    const checkDate = stripTime(date);
    return checkDate < stripTime(limitDate);
  };
  
  // 이전 주 이동
  const goToPreviousWeek = () => {
    const startDate = new Date(selectedWeek.startDate.replace(/\./g, '-'));
    
    // 이전 주의 시작일 계산
    const prevWeekStartDate = new Date(startDate);
    prevWeekStartDate.setDate(startDate.getDate() - 7);
    
    // 제한 날짜보다 이전인지 확인
    if (isBeforeLimitDate(prevWeekStartDate)) {
      // 제한 날짜로 설정
      if (startDate.getTime() !== limitDate.getTime()) {
        const endDate = new Date(limitDate);
        endDate.setDate(limitDate.getDate() + 4); // 수요일부터 일요일까지 (5일)
        
        onWeekChange({
          startDate: formatDate(limitDate),
          endDate: formatDate(endDate)
        });
        
        // 달력 날짜도 함께 업데이트
        setCalendarDate(new Date(limitDate.getFullYear(), limitDate.getMonth(), 1));
      }
      return;
    }
    
    startDate.setDate(startDate.getDate() - 7);
    
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    
    onWeekChange({
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    });
    
    // 달력 날짜도 함께 업데이트
    setCalendarDate(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
  };
  
  // 다음 주 이동
  const goToNextWeek = () => {
    // 이번 주인지 확인
    if (isCurrentWeek()) {
      return; // 이번 주라면 다음 주로 이동하지 않음
    }
    
    const startDate = new Date(selectedWeek.startDate.replace(/\./g, '-'));
    startDate.setDate(startDate.getDate() + 7);
    
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    
    onWeekChange({
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    });
    
    // 달력 날짜도 함께 업데이트
    setCalendarDate(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
  };
  
  // 이번 주로 이동
  const goToCurrentWeek = () => {
    const currentWeekStart = getCurrentWeekStart();
    const endDate = new Date(currentWeekStart);
    endDate.setDate(currentWeekStart.getDate() + 6);
    
    onWeekChange({
      startDate: formatDate(currentWeekStart),
      endDate: formatDate(endDate)
    });
    
    // 달력 날짜도 함께 업데이트
    setCalendarDate(new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), 1));
  };

  useEffect(() => {
    if(mainClass){
      goToCurrentWeek();
    }
  }, [mainClass]);
  
  // 현재 날짜가 포함된 주인지 확인
  const isCurrentWeek = () => {
    const currentWeekStart = getCurrentWeekStart();
    const selectedStartDate = new Date(selectedWeek.startDate.replace(/\./g, '-'));
    
    return (
      currentWeekStart.getFullYear() === selectedStartDate.getFullYear() &&
      currentWeekStart.getMonth() === selectedStartDate.getMonth() &&
      currentWeekStart.getDate() === selectedStartDate.getDate()
    );
  };
  
  // 날짜 포맷 변환 (YYYY.MM.DD)
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };
  
  // 날짜 비교를 위해 시간 정보를 제거한 날짜 생성
  const stripTime = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };
  
  // 달력의 월 변경
  const changeMonth = (increment) => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + increment);
    
    // 이번 달보다 미래의 달로는 이동할 수 없음
    const newMonth = newDate.getFullYear() * 12 + newDate.getMonth();
    if (increment > 0 && newMonth > currentMonth) {
      return;
    }
    
    // 제한 날짜보다 이전의 달로는 이동할 수 없음
    if (increment < 0 && newDate < new Date(limitDate.getFullYear(), limitDate.getMonth(), 1)) {
      return;
    }
    
    setCalendarDate(newDate);
  };
  
  // 달력의 다음 달 버튼이 비활성화되어야 하는지 확인
  const isNextMonthDisabled = () => {
    const nextMonth = calendarDate.getFullYear() * 12 + calendarDate.getMonth() + 1;
    return nextMonth > currentMonth;
  };
  
  // 달력의 이전 달 버튼이 비활성화되어야 하는지 확인
  const isPrevMonthDisabled = () => {
    const prevMonth = new Date(calendarDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    return prevMonth < new Date(limitDate.getFullYear(), limitDate.getMonth(), 1);
  };
  
  // 날짜가 이번 주보다 미래인지 확인
  const isFutureDate = (date) => {
    const checkDate = stripTime(date);
    const currentWeekEnd = stripTime(getCurrentWeekStart());
    currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);
    
    return checkDate > currentWeekEnd;
  };
  
  // 달력에서 주 선택
  const selectWeek = (date) => {
    const selected = new Date(date);
    
    // 이번 주보다 미래의 주는 선택할 수 없음
    if (isFutureDate(selected)) {
      return;
    }
    
    // 제한 날짜보다 이전의 주는 선택할 수 없음
    if (isBeforeLimitDate(selected)) {
      return;
    }
    
    // 일반적인 월요일-일요일 주간 계산
    const day = selected.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    
    let startDate = new Date(selected);
    startDate.setDate(selected.getDate() + diffToMonday);
    
    // 시작일이 제한 날짜보다 이전이면 제한 날짜부터 시작
    if (isBeforeLimitDate(startDate)) {
      // 제한 날짜가 수요일(1월 1일)인 경우 특별 처리
      startDate = new Date(limitDate);
      
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 4); // 수요일부터 일요일까지 (5일)
      
      onWeekChange({
        startDate: formatDate(startDate),
        endDate: formatDate(endDate)
      });
    } else {
      // 정상적인 월요일-일요일 주간
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      
      onWeekChange({
        startDate: formatDate(startDate),
        endDate: formatDate(endDate)
      });
    }
    
    setShowCalendar(false);
  };
  
  // 2025년 1월 1일 특별 주간 선택
  const selectFirstWeekOf2025 = () => {
    const startDate = new Date(limitDate); // 2025-01-01 (수요일)
    const endDate = new Date(limitDate);
    endDate.setDate(limitDate.getDate() + 4); // 2025-01-05 (일요일)
    
    onWeekChange({
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    });
    
    setCalendarDate(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
    setShowCalendar(false);
  };
  
  // 주가 호버되었을 때
  const handleWeekHover = (date) => {
    if (!date) {
      setHoveredWeek(null);
      return;
    }
    
    const hoverDate = new Date(date);
    const day = hoverDate.getDay();
    
    // 2025년 1월 첫 주에 해당하는 특별 케이스
    if (
      hoverDate.getFullYear() === 2025 && 
      hoverDate.getMonth() === 0 && 
      hoverDate.getDate() >= 1 && 
      hoverDate.getDate() <= 5
    ) {
      // 제한 날짜(수요일)부터 일요일까지
      const startDate = new Date(limitDate);
      const endDate = new Date(limitDate.getFullYear(), limitDate.getMonth(), 5); // 1월 5일(일요일)
      
      setHoveredWeek({
        startDate,
        endDate
      });
      return;
    }
    
    // 일반적인 월요일-일요일 주간 계산
    const diffToMonday = day === 0 ? -6 : 1 - day;
    
    const startDate = new Date(hoverDate);
    startDate.setDate(hoverDate.getDate() + diffToMonday);
    
    // 시작일이 제한 날짜보다 이전이면, 제한 날짜부터 일요일까지로 설정
    if (isBeforeLimitDate(startDate)) {
      // 2025년 1월인 경우 첫 주에 대한 특별 처리
      if (
        hoverDate.getFullYear() === 2025 && 
        hoverDate.getMonth() === 0 && 
        hoverDate.getDate() <= 12 // 첫째, 둘째 주에 해당
      ) {
        const weekOfMonth = Math.ceil(hoverDate.getDate() / 7);
        
        if (weekOfMonth === 1) {
          // 첫째 주 (1일~5일)
          const modifiedStartDate = new Date(2025, 0, 1); // 첫 주 시작일 (수)
          const modifiedEndDate = new Date(2025, 0, 5);   // 첫 주 종료일 (일)
          
          setHoveredWeek({
            startDate: modifiedStartDate,
            endDate: modifiedEndDate
          });
        } else {
          // 둘째 주 (6일~12일), 월요일부터 일요일까지
          const modifiedStartDate = new Date(2025, 0, 6); // 둘째 주 시작일 (월)
          const modifiedEndDate = new Date(2025, 0, 12);  // 둘째 주 종료일 (일)
          
          setHoveredWeek({
            startDate: modifiedStartDate,
            endDate: modifiedEndDate
          });
        }
        return;
      }
      
      // 다른 경우는 제한 날짜부터 해당 주의 일요일까지
      const modifiedStartDate = new Date(limitDate);
      const endDate = new Date(hoverDate);
      // 이번 주의 일요일 계산
      endDate.setDate(endDate.getDate() + (7 - endDate.getDay()) % 7);
      
      setHoveredWeek({
        startDate: modifiedStartDate,
        endDate
      });
      return;
    }
    
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    
    setHoveredWeek({
      startDate,
      endDate
    });
  };
  
  // 날짜가 제한 날짜보다 이전이거나 이번 주보다 미래인지 확인
  const isDateDisabled = (date) => {
    return isBeforeLimitDate(date) || isFutureDate(date);
  };
  
  // 날짜가 호버된 주에 포함되는지 확인
  const isDateInHoveredWeek = (date) => {
    if (!hoveredWeek) return false;
    
    const checkDate = stripTime(date);
    const startDate = stripTime(hoveredWeek.startDate);
    const endDate = stripTime(hoveredWeek.endDate);
    
    return (
      checkDate >= startDate &&
      checkDate <= endDate
    );
  };
  
  // 날짜가 선택된 주에 포함되는지 확인
  const isDateInSelectedWeek = (date) => {
    if (!selectedWeek.startDate || !selectedWeek.endDate) return false;
    
    const checkDate = stripTime(date);
    const startDate = stripTime(new Date(selectedWeek.startDate.replace(/\./g, '-')));
    const endDate = stripTime(new Date(selectedWeek.endDate.replace(/\./g, '-')));
    
    return (
      checkDate >= startDate &&
      checkDate <= endDate
    );
  };
  
  // 달력 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // 달력 렌더링
  const renderCalendar = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    
    // 월의 첫째 날과 마지막 날 구하기
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // 달력에 표시할 날짜 배열 생성
    const daysArray = [];
    
    // 첫째 날 이전의 빈 칸 채우기
    const firstDayOfWeek = firstDay.getDay(); // 0: 일요일, 1: 월요일, ...
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const prevMonthDay = prevMonthLastDay - i;
      const date = new Date(year, month - 1, prevMonthDay);
      daysArray.push({
        date,
        day: prevMonthDay,
        isCurrentMonth: false
      });
    }
    
    // 현재 월의 날짜 채우기
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      daysArray.push({
        date,
        day: i,
        isCurrentMonth: true
      });
    }
    
    // 마지막 날 이후의 빈 칸 채우기
    const lastDayOfWeek = lastDay.getDay();
    const daysToAdd = 6 - lastDayOfWeek;
    
    for (let i = 1; i <= daysToAdd; i++) {
      const date = new Date(year, month + 1, i);
      daysArray.push({
        date,
        day: i,
        isCurrentMonth: false
      });
    }
    
    // 주 단위로 날짜 배열 나누기
    const weeksArray = [];
    for (let i = 0; i < daysArray.length; i += 7) {
      weeksArray.push(daysArray.slice(i, i + 7));
    }
    
    // 2025년 1월 첫 주에 대한 특별 처리
    const isJanuary2025 = year === 2025 && month === 0;
    
    return (
      <div className="bg-white rounded-lg shadow-lg p-4" ref={calendarRef}>
        <div className="flex justify-between items-center mb-4">
          <button 
            onClick={() => changeMonth(-1)}
            disabled={isPrevMonthDisabled()}
            className={`p-1 rounded-full ${isPrevMonthDisabled() ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-200'}`}
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <div className="font-semibold">
            {year}년 {month + 1}월
          </div>
          <button 
            onClick={() => changeMonth(1)}
            className={`p-1 rounded-full ${isNextMonthDisabled() ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-200'}`}
            disabled={isNextMonthDisabled()}
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
        
        {isJanuary2025 && (
          <div className="mb-2">
            <button 
              onClick={selectFirstWeekOf2025}
              className="w-full text-sm py-1 px-2 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
            >
              2025년 1월 첫 주 (1.1~1.5)
            </button>
          </div>
        )}
        
        <div className="grid grid-cols-7 gap-1 text-center font-medium mb-2">
          <div className="text-red-500">일</div>
          <div>월</div>
          <div>화</div>
          <div>수</div>
          <div>목</div>
          <div>금</div>
          <div className="text-blue-500">토</div>
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {weeksArray.map((week, weekIndex) => (
            <React.Fragment key={weekIndex}>
              {week.map((day, dayIndex) => {
                const isDisabled = isDateDisabled(day.date);
                // 2025년 1월 1일-5일 특별 처리
                const isSpecialFirstWeek = day.date.getFullYear() === 2025 && 
                                          day.date.getMonth() === 0 && 
                                          day.date.getDate() >= 1 && 
                                          day.date.getDate() <= 5;
                
                return (
                  <div
                    key={dayIndex}
                    className={`
                      p-2 text-center rounded
                      ${!day.isCurrentMonth ? 'text-gray-400' : ''}
                      ${isDisabled && !isSpecialFirstWeek ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}
                      ${isDateInHoveredWeek(day.date) && (!isDisabled || isSpecialFirstWeek) ? 'bg-blue-100' : ''}
                      ${isDateInSelectedWeek(day.date) ? 'bg-blue-200 font-bold' : ''}
                      ${isBeforeLimitDate(day.date) && !isSpecialFirstWeek ? 'line-through' : ''}
                    `}
                    onClick={() => {
                      if (isSpecialFirstWeek) {
                        selectFirstWeekOf2025();
                      } else if (!isDisabled) {
                        selectWeek(day.date);
                      }
                    }}
                    onMouseEnter={() => {
                      if (isSpecialFirstWeek) {
                        // 2025년 1월 1일-5일 호버 효과
                        const startDate = new Date(2025, 0, 1);
                        const endDate = new Date(2025, 0, 5);
                        setHoveredWeek({ startDate, endDate });
                      } else if (!isDisabled) {
                        handleWeekHover(day.date);
                      }
                    }}
                    onMouseLeave={() => handleWeekHover(null)}
                  >
                    {day.day}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };
  
  // 선택된 주의 시작일이 제한 날짜보다 이전인지 확인
  const isSelectedWeekBeforeLimit = () => {
    if (!selectedWeek.startDate) return false;
    
    const startDate = new Date(selectedWeek.startDate.replace(/\./g, '-'));
    return isBeforeLimitDate(startDate);
  };
  
  // 2025년 1월 첫 주(제한 날짜 기준)인지 확인
  const isFirstWeekOf2025 = () => {
    if (!selectedWeek.startDate) return false;
    
    const startDate = new Date(selectedWeek.startDate.replace(/\./g, '-'));
    return (
      startDate.getFullYear() === 2025 &&
      startDate.getMonth() === 0 &&
      startDate.getDate() === 1
    );
  };
  
  return (
    <div className="mb-8 relative  mt-[65px]">
      <div className="flex items-center justify-center space-x-4">
        <NavigationButton
          onClick={goToPreviousWeek}
          disabled={isFirstWeekOf2025()}
          className={`flex items-center justify-center w-10 h-10 rounded-full 
            ${isFirstWeekOf2025() 
              ? 'bg-gray-100 cursor-not-allowed' 
              : 'bg-blue-100 hover:bg-blue-200'
            }`}
          direction="prev"
        >
        </NavigationButton>
        
        <button
          onClick={() => setShowCalendar(!showCalendar)}
          className="flex items-center justify-center px-4 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50"
        >
          <span className="text-lg font-medium">
            {selectedWeek.startDate.replace(/\./g, '. ')} ~ {selectedWeek.endDate.replace(/\./g, '. ')}
          </span>
        </button>
        
        <NavigationButton
          onClick={goToNextWeek}
          className={`flex items-center justify-center w-10 h-10 rounded-full 
            ${isCurrentWeek() 
              ? 'bg-gray-100 cursor-not-allowed' 
              : 'bg-blue-100 hover:bg-blue-200'
            }`}
          disabled={isCurrentWeek()}
        >
          <ChevronRightIcon className={`w-5 h-5 ${isCurrentWeek() ? 'text-gray-400' : 'text-blue-700'}`} />
        </NavigationButton>
      </div>
      
      {/* 이번주 버튼 */}
      <div className="flex justify-center mt-2">
        <button
          onClick={goToCurrentWeek}
          disabled={isCurrentWeek()}
          className={`text-sm px-3 py-1 rounded-md ${
            isCurrentWeek() 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-[#D7E5FE] text-[#5A85FF] hover:bg-blue-100 font-[700] '
          }`}
        >
          이번주
        </button>
      </div>
      
      {showCalendar && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-10 min-w-[330px]">
          {renderCalendar()}
        </div>
      )}
    </div>
  );
};

export default WeekSelector; 